import React, { useEffect, useState } from "react";
import { ModalProps } from "react-bootstrap";
import { BagCheck, GeoAlt, Person, X } from "react-bootstrap-icons";
import { ReferralParticipant, ReferralParticipantActivity } from "../../types";
import * as Styled from "./ParticipantActivityModal.styled";

interface Props extends ModalProps {
  participant: ReferralParticipant | null;
  onLoad: (participantId: string) => Promise<ReferralParticipantActivity>;
}

const dateLabel = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : "Date unavailable";
const priceLabel = (amount: number, frequency?: string | null) =>
  `$${Number(amount || 0).toFixed(2)}${frequency ? ` / ${frequency}` : ""}`;

export const ParticipantActivityModal: React.FC<Props> = ({ participant, onLoad, ...props }) => {
  const [activity, setActivity] = useState<ReferralParticipantActivity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!props.show || !participant) return;
    let active = true;
    setLoading(true);
    setError("");
    setActivity(null);
    onLoad(participant.id)
      .then((data) => {
        if (active) setActivity(data);
      })
      .catch(() => {
        if (active) setError("The referrer activity could not be loaded.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [props.show, participant, onLoad]);

  return (
    <Styled.ActivityModal {...props} centered keyboard={false}>
      <Styled.ModalWrapper>
        <Styled.Header>
          <div>
            <Styled.ModalTitle>Referral Activity</Styled.ModalTitle>
            <Styled.Subtitle>
              {participant?.email} · {participant?.referralCode}
            </Styled.Subtitle>
          </div>
          <Styled.CloseButton type="button" onClick={() => props.onHide?.()} aria-label="Close">
            <X size={22} />
          </Styled.CloseButton>
        </Styled.Header>

        {loading && <Styled.Message>Loading registrations and purchases…</Styled.Message>}
        {error && <Styled.ErrorMessage>{error}</Styled.ErrorMessage>}
        {!loading && !error && activity?.registrations.length === 0 && (
          <Styled.Message>No attributed registrations yet.</Styled.Message>
        )}

        {!loading && activity && activity.registrations.length > 0 && (
          <Styled.RegistrationList>
            {activity.registrations.map((registration) => (
              <Styled.RegistrationCard key={registration.userId}>
                <Styled.MemberHeader>
                  <Styled.MemberIdentity>
                    <Styled.Avatar>
                      <Person />
                    </Styled.Avatar>
                    <div>
                      <Styled.MemberName>{registration.name}</Styled.MemberName>
                      <Styled.MemberEmail>{registration.email}</Styled.MemberEmail>
                    </div>
                  </Styled.MemberIdentity>
                  <Styled.RegisteredAt>
                    {registration.registeredThroughReferral
                      ? `Registered through referral ${dateLabel(registration.registeredAt)}`
                      : `Existing member · attributed ${dateLabel(registration.firstCapturedAt)}`}
                  </Styled.RegisteredAt>
                </Styled.MemberHeader>
                {registration.landingUrl && (
                  <Styled.LandingUrl>
                    <GeoAlt /> {registration.landingUrl}
                  </Styled.LandingUrl>
                )}
                <Styled.PurchaseHeader>
                  <span>Purchases</span>
                  <Styled.CountBadge>{registration.purchases.length}</Styled.CountBadge>
                </Styled.PurchaseHeader>
                {registration.purchases.length > 0 ? (
                  <Styled.PurchaseList>
                    {registration.purchases.map((purchase, index) => (
                      <Styled.PurchaseRow
                        key={`${purchase.paymentIntentId || purchase.id}-${index}`}
                      >
                        <Styled.PurchaseIcon>
                          <BagCheck />
                        </Styled.PurchaseIcon>
                        <Styled.PurchaseCopy>
                          <Styled.PurchaseName>{purchase.name}</Styled.PurchaseName>
                          <Styled.PurchaseDate>
                            {dateLabel(purchase.purchasedDate)}
                            {purchase.isRecurring ? " · Recurring" : " · One-time"}
                          </Styled.PurchaseDate>
                        </Styled.PurchaseCopy>
                        <Styled.PurchasePrice>
                          {priceLabel(
                            purchase.amount,
                            purchase.isRecurring ? purchase.frequency : null
                          )}
                        </Styled.PurchasePrice>
                      </Styled.PurchaseRow>
                    ))}
                  </Styled.PurchaseList>
                ) : (
                  <Styled.NoPurchases>No purchases recorded for this member.</Styled.NoPurchases>
                )}
              </Styled.RegistrationCard>
            ))}
          </Styled.RegistrationList>
        )}
      </Styled.ModalWrapper>
    </Styled.ActivityModal>
  );
};
